// ==========================================================
//   MOTOR DE CÁLCULO IRPH (sistema francés)
// ==========================================================
import { SERIE } from './serieIRPH';

export { SERIE };

// ==========================================================
// UTILIDADES
// ==========================================================

export function ym(date) {
  return date.toISOString().slice(0, 7);
}
export function addMonths(date, n) {
  return new Date(date.getFullYear(), date.getMonth() + n, 1);
}
export function parseYM(s) {
  const [y, m] = s.split('-').map(Number);
  return new Date(y, m - 1, 1);
}
export function fmt(n, dec = 2) {
  if (n === null || n === undefined || isNaN(n)) return '-';
  return n.toLocaleString('es-ES', { minimumFractionDigits: dec, maximumFractionDigits: dec });
}
export function eur(n) {
  return fmt(n, 2) + ' €';
}
export function pct(n) {
  return fmt(n, 3) + ' %';
}

export function aplicarRedondeo(tipo, paso, alza) {
  if (!paso || paso <= 0) return tipo;
  if (alza) return Math.ceil(tipo / paso) * paso;
  return Math.round(tipo / paso) * paso;
}

// El parámetro `serie` permite inyectar datos desde Supabase manteniendo el
// SERIE estático como default (fallback offline / tests).
export function getIndice(indId, ymKey, serie = SERIE) {
  if (indId === '__NULO__') return 0;
  const r = serie[ymKey];
  if (!r) return null;
  if (indId === 'IRPH_CE') return r.ce;
  if (indId === 'IRPH_CAJAS') return r.cajas;
  if (indId === 'IRPH_BANCOS') return r.bancos;
  if (indId === 'EURIBOR12') return r.eur;
  // El MIBOR dejó de ser la referencia oficial con la llegada del euro: solo se
  // considera válido hasta dic/1999. A partir de ene/2000 se ignora aunque la
  // serie conserve datos posteriores (no intervienen en ningún cálculo).
  const mibor = ymKey > '1999-12' ? null : r.mibor;
  if (indId === 'MIBOR') return mibor;
  // Índice de mercado "continuo" (variante 1999 = Euríbor): Euríbor desde su
  // publicación (ene/1999) y MIBOR para los meses anteriores en que no existía.
  if (indId === 'EUR_MIBOR') {
    if (r.eur !== null && r.eur !== undefined) return r.eur;
    return mibor;
  }
  // Mercado "continuo" variante 1999 = MIBOR: MIBOR mientras es vigente (hasta
  // dic/1999) y Euríbor a partir de 2000. Se usa en el panel de convivencia.
  if (indId === 'MIBOR_EUR') {
    if (mibor !== null && mibor !== undefined) return mibor;
    return r.eur;
  }
  return null;
}

// Valor "vigente" de un índice para un mes: el del último mes publicado que sea
// anterior o igual. Aplica el criterio de que un tipo oficial rige hasta que se
// publica el siguiente; imprescindible porque la serie es SEMESTRAL antes de
// 2000 (solo jun/dic) y las revisiones rara vez caen exactamente en esos meses.
export function valorVigente(indId, ymKey, serie = SERIE) {
  let mejor = null;
  for (const k in serie) {
    if (k > ymKey) continue;
    const v = getIndice(indId, k, serie);
    if (v === null || v === undefined) continue;
    if (!mejor || k > mejor.k) mejor = { v, k };
  }
  return mejor;
}

// ¿Hay algún dato POSTERIOR para el índice? Distingue un hueco interior por
// granularidad de la serie (→ arrastrar el último valor publicado) de un índice
// ya descatalogado/suprimido (→ aplicar la regla de sustitución que corresponda).
export function existeDatoPosterior(indId, ymKey, serie = SERIE) {
  for (const k in serie) {
    if (k <= ymKey) continue;
    const v = getIndice(indId, k, serie);
    if (v !== null && v !== undefined) return true;
  }
  return false;
}

// Resuelve el valor del índice IRPH para un mes determinado de la hipoteca,
// teniendo en cuenta la supresión 2013 y la regla elegida por el usuario.
export function resolverIRPH(indId, fechaRev, ultimoConocido, trasSupresion, serie = SERIE) {
  const k = ym(fechaRev);
  const v = getIndice(indId, k, serie);
  if (v !== null && v !== undefined) return { valor: v, fuente: indId, k };

  // (1) Hueco INTERIOR de la serie (p. ej. una revisión de un préstamo de los
  // años 90 que no cae en jun/dic): mientras el índice siga publicándose más
  // adelante, se arrastra el último valor vigente hasta la siguiente revisión.
  if (existeDatoPosterior(indId, k, serie)) {
    const cf = valorVigente(indId, k, serie);
    if (cf) return { valor: cf.v, fuente: indId, k: cf.k };
  }

  // (2) IRPH Cajas/Bancos suprimido el 1/11/2013: regla de sustitución elegida.
  if (indId === 'IRPH_CAJAS' || indId === 'IRPH_BANCOS') {
    if (trasSupresion === 'DA15') {
      const ce = getIndice('IRPH_CE', k, serie);
      if (ce !== null) return { valor: ce, fuente: 'IRPH_CE (DA15)', k };
    } else if (trasSupresion === 'congelar') {
      return { valor: ultimoConocido.v, fuente: 'congelado oct/2013', k: ultimoConocido.k };
    } else if (trasSupresion === 'euribor') {
      const e = getIndice('EURIBOR12', k, serie);
      if (e !== null) return { valor: e, fuente: 'EURIBOR12 (sust.)', k };
    } else if (trasSupresion === 'fijo') {
      return { valor: ultimoConocido.v, fuente: 'fijo último IRPH', k: ultimoConocido.k };
    }
  }

  // (3) Índice descatalogado sin regla especial (p. ej. MIBOR puro tras
  // ene/2000): se congela en su último valor publicado en vez de caer a 0%.
  const ult = valorVigente(indId, k, serie);
  if (ult) return { valor: ult.v, fuente: indId + ' (último publicado)', k: ult.k };

  // (4) Sin ningún dato anterior (p. ej. Euríbor antes de jun/1999): último
  // recurso. Para préstamos pre-2000 usa "Euríbor / MIBOR (mercado continuo)".
  return { valor: ultimoConocido.v, fuente: 'ext. último', k: ultimoConocido.k };
}

// ==========================================================
// MOTOR DE CÁLCULO (sistema francés)
// ==========================================================
export function calcularCuotaFrancesa(capital, tasaMensual, n) {
  if (tasaMensual === 0) return capital / n;
  return (capital * tasaMensual) / (1 - Math.pow(1 + tasaMensual, -n));
}

export function simular(opciones) {
  const {
    capital, plazo, fechaFirma, periodRev, offsetRev,
    carencia, indice, diff, tipoFijoMeses, tipoFijoValor,
    suelo, techo, fechaCorte, anticipos,
    redondeoPaso, redondeoAlza, trasSupresion,
    serie = SERIE,
  } = opciones;

  let saldo = capital;
  let tipoActual = null;
  let cuota = null;
  let mesesPendientes = plazo;
  const filas = [];
  let ultimoIndice = { v: null, k: null };

  // Último valor IRPH cajas/bancos conocido (para el caso "congelar"/fijo)
  if (indice === 'IRPH_CAJAS' || indice === 'IRPH_BANCOS') {
    for (let i = 0; i < plazo; i++) {
      const f = addMonths(fechaFirma, i);
      const k = ym(f);
      const v = getIndice(indice, k, serie);
      if (v !== null) ultimoIndice = { v, k };
    }
  }

  for (let i = 0; i < plazo; i++) {
    const fechaPago = addMonths(fechaFirma, i);
    if (fechaCorte && fechaPago > fechaCorte) break;

    // ¿Hay revisión este mes?
    const esRev = i === 0 || i % periodRev === 0;
    if (esRev) {
      let tipoNuevo;
      if (i < tipoFijoMeses) {
        tipoNuevo = tipoFijoValor;
      } else {
        const fechaIndice = addMonths(fechaPago, offsetRev);
        const r = resolverIRPH(indice, fechaIndice, ultimoIndice, trasSupresion, serie);
        const raw = r.valor === null || r.valor === undefined ? 0 : r.valor;
        tipoNuevo = raw + diff;
        tipoNuevo = aplicarRedondeo(tipoNuevo, redondeoPaso, redondeoAlza);
        if (suelo !== null && tipoNuevo < suelo) tipoNuevo = suelo;
        if (techo !== null && tipoNuevo > techo) tipoNuevo = techo;
      }
      tipoActual = tipoNuevo;
      cuota = calcularCuotaFrancesa(saldo, tipoActual / 100 / 12, mesesPendientes);
    }

    // Carencia inicial: sólo intereses
    const interes = saldo * (tipoActual / 100 / 12);
    let amort;
    let pago;
    if (i < carencia) {
      amort = 0;
      pago = interes;
    } else {
      amort = Math.min(cuota - interes, saldo);
      pago = interes + amort;
    }

    // Aplicar amortización anticipada si toca
    let extra = 0;
    let efectoExtra = '';
    const k = ym(fechaPago);
    const ant = anticipos.find((a) => a.mes === k);
    if (ant) {
      extra = Math.min(ant.imp, saldo - amort);
      saldo -= amort + extra;
      if (ant.efecto === 'cuota') {
        // recalcular cuota con saldo y meses pendientes restantes
        mesesPendientes--;
        if (mesesPendientes > 0) cuota = calcularCuotaFrancesa(saldo, tipoActual / 100 / 12, mesesPendientes);
      } else {
        // reducir plazo: dejamos cuota igual, recalculamos mesesPendientes
        mesesPendientes--;
        if (saldo > 0 && cuota > 0 && tipoActual > 0) {
          const r = tipoActual / 100 / 12;
          mesesPendientes = Math.ceil(Math.log(cuota / (cuota - saldo * r)) / Math.log(1 + r));
        } else mesesPendientes--;
      }
      efectoExtra = ant.efecto;
    } else {
      saldo -= amort;
      mesesPendientes--;
    }
    if (saldo < 0.01) saldo = 0;

    filas.push({
      i, fecha: fechaPago, k: ym(fechaPago),
      tipo: tipoActual, cuota: pago, interes, amort, saldo, extra, efectoExtra,
    });

    if (saldo <= 0.01) break;
  }
  return filas;
}

export function calcularTAEAproximada(capital, filas) {
  if (!filas.length) return 0;
  // TAE aprox: media ponderada del tipo aplicado durante el periodo
  let suma = 0;
  const n = filas.length;
  for (const f of filas) suma += f.tipo;
  return suma / n;
}

export function totales(filas, n) {
  const tot = { int: 0, cap: 0 };
  for (let i = 0; i < n; i++) {
    tot.int += filas[i].interes;
    tot.cap += filas[i].amort + (filas[i].extra || 0);
  }
  tot.cuotaUlt = filas[n - 1]?.cuota ?? 0;
  tot.saldo = filas[n - 1]?.saldo ?? 0;
  return tot;
}

// Agregado anual reutilizable por tabla anual, CSV y PDF.
// Devuelve filas ordenadas por año con intereses/capital acumulados.
export function agregarAnual(filasIRPH, filasAlt, filasAltB, corteIdx, altBOn) {
  const acum = {};
  for (let i = 0; i < corteIdx; i++) {
    const y = filasIRPH[i].fecha.getFullYear();
    acum[y] = acum[y] || { intI: 0, intA: 0, intB: 0, capI: 0, capA: 0, capB: 0 };
    acum[y].intI += filasIRPH[i].interes;
    acum[y].intA += filasAlt[i].interes;
    acum[y].capI += filasIRPH[i].amort + (filasIRPH[i].extra || 0);
    acum[y].capA += filasAlt[i].amort + (filasAlt[i].extra || 0);
    if (altBOn) {
      acum[y].intB += filasAltB[i].interes;
      acum[y].capB += filasAltB[i].amort + (filasAltB[i].extra || 0);
    }
  }
  let acumA = 0;
  let acumB = 0;
  return Object.keys(acum)
    .sort()
    .map((y) => {
      const a = acum[y];
      const sA = a.intI - a.intA;
      const sB = altBOn ? a.intI - a.intB : null;
      acumA += sA;
      if (altBOn) acumB += sB;
      return { year: y, ...a, sA, sB, acumA, acumB };
    });
}

// ==========================================================
// ORQUESTACIÓN: simular ambos escenarios y producir resultados.
// `form` replica los campos del formulario del HTML original.
// `serie` permite inyectar datos vivos (Supabase). Default = SERIE estático.
// ==========================================================
export function ejecutarSimulacion(form, serie = SERIE) {
  const capital = parseFloat(form.capital);
  // Tope defensivo: el motor recorre un bucle de `plazo` meses. Sin este
  // límite, un valor enorme (accidental o malicioso, con fecha de corte
  // vacía) podría congelar el navegador. 1200 meses = 100 años, sobra.
  let plazo = parseInt(form.plazo, 10);
  if (!Number.isFinite(plazo) || plazo < 1) plazo = 1;
  if (plazo > 1200) plazo = 1200;
  const fechaFirma = parseYM(form.fechaFirma);
  const periodRev = parseInt(form.periodRev, 10);
  const offsetRev = parseInt(form.offsetRev, 10);
  const carencia = parseInt(form.carencia, 10) || 0;
  const indice = form.indiceIRPH;
  const diff = parseFloat(form.diffIRPH) || 0;
  const tipoFijoMeses = parseInt(form.tipoFijoMeses, 10) || 0;
  const tipoFijoValor = parseFloat(form.tipoFijoValor) || 0;
  const suelo = form.suelo === '' || form.suelo === null || form.suelo === undefined
    ? null
    : parseFloat(form.suelo);
  const techo = form.techo === '' || form.techo === null || form.techo === undefined
    ? null
    : parseFloat(form.techo);
  const fechaCorte = form.fechaCorte ? parseYM(form.fechaCorte) : null;
  const redondeoPaso = parseFloat(form.redondeo) || 0;
  const redondeoAlza = !!form.redondeoAlza;
  const trasSupresion = form.trasSupresion;
  const anticipos = (form.anticipos || [])
    .filter((a) => a.mes && parseFloat(a.imp) > 0)
    .map((a) => ({ mes: a.mes, imp: parseFloat(a.imp), efecto: a.efecto }));

  const indAlt = form.indiceAlt;
  const diffAlt = parseFloat(form.diffAlt) || 0;
  const altBEnabled = !!form.altBEnabled;
  const indAltB = form.indiceAltB;
  const diffAltB = parseFloat(form.diffAltB) || 0;

  const opcsBase = {
    capital, plazo, fechaFirma, periodRev, offsetRev, carencia,
    tipoFijoMeses, tipoFijoValor, suelo, techo, fechaCorte, anticipos,
    redondeoPaso, redondeoAlza, trasSupresion, serie,
  };

  const filasIRPH = simular({ ...opcsBase, indice, diff });
  const filasAlt = indAlt === 'NULO'
    ? simular({ ...opcsBase, indice: '__NULO__', diff: diffAlt })
    : simular({ ...opcsBase, indice: indAlt, diff: diffAlt });
  const filasAltB = altBEnabled
    ? indAltB === 'NULO'
      ? simular({ ...opcsBase, indice: '__NULO__', diff: diffAltB })
      : simular({ ...opcsBase, indice: indAltB, diff: diffAltB })
    : null;

  return {
    filasIRPH, filasAlt, filasAltB, opcsBase,
    indice, indAlt, indAltB, diff, diffAlt, diffAltB, altBEnabled,
  };
}
